// src/App.js
import React, { useEffect, useState, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  Circle,
} from "react-leaflet";
import L from "leaflet";
import { FaRegCopy, FaWhatsapp, FaLinkedin } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/free-solid-svg-icons";
import { faXTwitter } from "@fortawesome/free-brands-svg-icons";
import ReactDOMServer from "react-dom/server";

import "./App.css";
import "leaflet/dist/leaflet.css";

delete L.Icon.Default.prototype._getIconUrl;

const locationIcon = new L.DivIcon({
  className: "custom-marker",
  html: ReactDOMServer.renderToString(
    <FontAwesomeIcon
      icon={faLocationDot}
      style={{
        fontSize: "32px",
        color: "#e63946",
        filter: "drop-shadow(0 0 4px rgb(255, 255, 255))",
        height: "45px",
        width: "45px",
      }}
    />
  ),
  iconSize: [32, 32],
  iconAnchor: [23, 43],
});

const defaultCenter = { lat: 12.994745, lng: 80.233408 };
const DEFAULT_ZOOM = 19;


function getShortAddress(fullAddress) {
  if (!fullAddress) return "";
  const parts = fullAddress.split(",");
  return parts.slice(0, 3).join(",").trim();
}

function Get_LatLng_By_Digipin(vDigiPin) {
  vDigiPin = vDigiPin.replaceAll("-", "");
  if (vDigiPin.length !== 10) return "Invalid DIGIPIN";
  const Lgrid = [
    ["F", "C", "9", "8"],
    ["J", "3", "2", "7"],
    ["K", "4", "5", "6"],
    ["L", "M", "P", "T"],
  ];
  let MinLat = 2.5,
    MaxLat = 38.5,
    MinLng = 63.5,
    MaxLng = 99.5;
  const LatDivBy = 4,
    LngDivBy = 4;
  let Lat1, Lat2, Lng1, Lng2;
  for (let Lvl = 0; Lvl < 10; Lvl++) {
    const LatStep = (MaxLat - MinLat) / LatDivBy;
    const LngStep = (MaxLng - MinLng) / LngDivBy;
    let found = false;
    for (let r = 0; r < LatDivBy; r++) {
      for (let c = 0; c < LngDivBy; c++) {
        if (Lgrid[r][c] === vDigiPin.charAt(Lvl)) {
          Lat1 = MaxLat - LatStep * (r + 1);
          Lat2 = MaxLat - LatStep * r;
          Lng1 = MinLng + LngStep * c;
          Lng2 = MinLng + LngStep * (c + 1);
          MinLat = Lat1;
          MaxLat = Lat2;
          MinLng = Lng1;
          MaxLng = Lng2;
          found = true;
          break;
        }
      }
      if (found) break;
    }
    if (!found) return "Invalid DIGIPIN";
  }
  return { lat: (Lat1 + Lat2) / 2, lng: (Lng1 + Lng2) / 2 };
}

function Get_DIGIPIN(lat, lon) {
  const Lgrid = [
    ["F", "C", "9", "8"],
    ["J", "3", "2", "7"],
    ["K", "4", "5", "6"],
    ["L", "M", "P", "T"],
  ];
  let code = "",
    MinLat = 2.5,
    MaxLat = 38.5,
    MinLon = 63.5,
    MaxLon = 99.5;
  const Div = 4;
  for (let lvl = 1; lvl <= 10; lvl++) {
    const latStep = (MaxLat - MinLat) / Div;
    const lonStep = (MaxLon - MinLon) / Div;
    let row = 0,
      col = 0;
    for (let i = 0; i < Div; i++) {
      if (lat >= MaxLat - latStep * (i + 1) && lat < MaxLat - latStep * i) {
        row = i;
        break;
      }
    }
    for (let j = 0; j < Div; j++) {
      if (lon >= MinLon + lonStep * j && lon < MinLon + lonStep * (j + 1)) {
        col = j;
        break;
      }
    }
    code += Lgrid[row][col];
    if (lvl === 3 || lvl === 6) code += "-";
    MaxLat = MaxLat - latStep * row;
    MinLat = MaxLat - latStep;
    MinLon = MinLon + lonStep * col;
    MaxLon = MinLon + lonStep;
  }
  return code;
}

function ChangeView({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView([center.lat, center.lng], DEFAULT_ZOOM);
    }
  }, [center, map]);
  return null;
}

function App() {
  const [markerPos, setMarkerPos] = useState(null);
  const [mapSearchText, setMapSearchText] = useState("");
  const [digipin, setDigipin] = useState("");
  const [isLocating, setIsLocating] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    if (mapSearchText.length < 3) {
      setSuggestions([]);
      return;
    }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&accept-language=en&countrycodes=IN&q=${encodeURIComponent(
            mapSearchText
          )}`,
          {
            signal: ctrl.signal,
            headers: { "User-Agent": "mydigipin-app/1.0" },
          }
        );
        if (!res.ok) throw new Error("Autocomplete failed");
        setSuggestions(await res.json());
      } catch (e) {
        if (e.name !== "AbortError") console.error(e);
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [mapSearchText]);

  const updatePosition = (lat, lon, name) => {
    setMarkerPos({ lat, lng: lon });
    setMapSearchText(getShortAddress(name));
    setIsTyping(false);
    setDigipin(Get_DIGIPIN(lat, lon));
    setSuggestions([]);
  };

  const updateDigipinAndAddress = async (lat, lon) => {
    setDigipin(Get_DIGIPIN(lat, lon));
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&accept-language=en`,
        { headers: { "User-Agent": "mydigipin-app/1.0" } }
      );
      const j = await res.json();
      if (j.display_name) {
        setMapSearchText(getShortAddress(j.display_name));
        setIsTyping(false);
      }
    } catch (e) {
      console.error("Reverse geocode error:", e);
    }
  };

  useEffect(() => {
    handleGetLocation();
  }, []);

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation not supported.");
      setMarkerPos(defaultCenter); // fallback
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        updatePosition(lat, lng, "");
        updateDigipinAndAddress(lat, lng);
        setIsLocating(false);
      },
      (err) => {
        if (err.code === 1) {
        // PERMISSION_DENIED
        alert(
          "Location access was blocked.\n\nPlease click the lock icon in your browser's address bar, go to 'Site Settings', and allow location access."
        );
        } else if (err.code === 2) {
          alert("Position unavailable. Please check your network or try again.");
        } else if (err.code === 3) {
          alert("Location request timed out. Try again.");
        } else {
          alert("Unable to retrieve location.");
        }
        setMarkerPos(defaultCenter);
        setIsLocating(false);
      }
    );
  };

  const handleDigipinKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const coords = Get_LatLng_By_Digipin(digipin);
      if (coords && typeof coords === "object") {
        updatePosition(coords.lat, coords.lng, "");
        updateDigipinAndAddress(coords.lat, coords.lng);
      } else {
        alert("Invalid or out-of-range DIGIPIN");
      }
    }
  };

  const handleCopy = () =>
    digipin &&
    navigator.clipboard.writeText(digipin).then(
      () => alert("DIGIPIN copied!"),
      (err) => console.error("Copy failed", err)
    );

  const shareText = () =>
    `Location: ${mapSearchText || "N/A"}\n\nDigipin: ${digipin ||
      "N/A"}\n(Located using mydigipin.in)`;

  const handleWhatsAppShare = () =>
    window.open(
      `https://api.whatsapp.com/send?text=${encodeURIComponent(
        shareText()
      )}`,
      "_blank"
    );

  const handleTwitterShare = () =>
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        shareText()
      )}`,
      "_blank"
    );

  const handleLinkedinShare = () =>
    window.open(
      `https://www.linkedin.com/feed/?shareActive&mini=true&text=${encodeURIComponent(
        shareText()
      )}`,
      "_blank"
    );

  const handleSuggestionClick = (item) => {
    const lat = parseFloat(item.lat),
      lon = parseFloat(item.lon);
    updatePosition(lat, lon, item.display_name);
  };

  const clearFunc = () => {
    setMapSearchText("");
  };

  return (
    <div className="app-container">
      <div className="device-wrapper">
        <div className="device">
          <div className="device-header">
            <input
              type="text"
              className="map-search"
              placeholder="Search location"
              value={mapSearchText}
              onChange={(e) => {
                setMapSearchText(e.target.value);
                setIsTyping(true);
              }}
            />
            {isTyping && suggestions.length > 0 && (
              <ul className="suggestions">
                {suggestions.map((s) => (
                  <li
                    key={s.place_id}
                    className="suggestion-item"
                    onClick={() => handleSuggestionClick(s)}
                  >
                    {s.display_name}
                  </li>
                ))}
              </ul>
            )}
            <div
              onClick={clearFunc}
              className="clear-btn"
            >
              Clear
            </div>
          </div>

          <div className="device-map">
            {markerPos?(
                          <MapContainer
              center={[markerPos.lat, markerPos.lng]}
              zoom={DEFAULT_ZOOM}
              zoomControl={true}
              style={{ width: "100%", height: "100%" }}
            >
              <ChangeView center={markerPos} />
            <TileLayer
              attribution='<a href="https://goodmove.in" target="_blank" rel="noopener noreferrer">goodmove.in</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

              <Circle
                center={[markerPos.lat, markerPos.lng]}
                radius={13}
                pathOptions={{
                  color: "#6a5acf",
                  fillOpacity: 0.3,
                  weight: 2,
                  dashArray: "4",
                }}
              />
              <Marker
                position={[markerPos.lat, markerPos.lng]}
                icon={locationIcon}
                draggable
                eventHandlers={{
                  dragend(e) {
                    const { lat, lng } = e.target.getLatLng();
                    setMarkerPos({ lat, lng });
                    updateDigipinAndAddress(lat, lng);
                  },
                }}
              />
            </MapContainer>
            ):(
              <div style={{ padding: "20px", textAlign: "center" }}>
                Locating your position...
              </div>
            )}

          </div>

          <div className="device-footer">
            <button className="locate-btn" onClick={handleGetLocation}>
              {isLocating ? (
                <div className="spin-wrap">
                  <div className="spin-circle" /> Locating You...
                </div>
              ) : (
                "My Location"
              )}
            </button>
            <input
              type="text"
              className="digipin-input"
              placeholder="Enter DIGIPIN & press Enter"
              value={digipin}
              onChange={(e) => setDigipin(e.target.value.toUpperCase())}
              onKeyDown={handleDigipinKeyDown}
            />
            <div className="iconsShare">
              <FaRegCopy onClick={handleCopy} title="Copy DIGIPIN" className="fa-copy" />
              <FaWhatsapp onClick={handleWhatsAppShare} title="WhatsApp" className="fa-whatsapp" />
              <FontAwesomeIcon icon={faXTwitter} onClick={handleTwitterShare} title="Twitter" className="fa-x" />
              <FaLinkedin onClick={handleLinkedinShare} title="LinkedIn" className="fa-linkedin" />
            </div>
          </div>
        </div>

        <p className="info-row">
          {markerPos ? (
            <>
              Lat/Lng: {markerPos.lat.toFixed(6)}, {markerPos.lng.toFixed(6)} | DIGIPIN:{" "}
              {digipin || "N/A"} | Powered by{" "}
              <a href="https://goodmove.in" target="_blank" rel="noreferrer">
                goodmove.in
              </a>
            </>
          ) : (
            <>Getting location...</>
          )}
        </p>
      </div>
    </div>
  );
}

export default App;